import { createContext, useEffect, useMemo, useState } from "react";
import defaultTranslations from "@/translations/en.json";
import { generateTranslations } from "@/utils/translations";
import parse from "html-react-parser";
import { IS_DEVELOPMENT, IS_TEST } from "@/config";
import {
  Translations,
  TranslateParams,
  TranslationResult,
  TranslationProviderProps,
  TranslationContextType,
  TranslateOptions,
} from "./TranslationProviderProps";
import DOMPurify from "dompurify";

const showWarnings = IS_DEVELOPMENT && !IS_TEST;

export const TranslationContext = createContext<TranslationContextType | null>(
  null,
);

function interpolateParams(
  translation: string,
  params?: TranslateParams,
): string {
  return translation.replace(/{{\s*([^}\s]+)\s*}}/g, (_match, key) => {
    const value = params?.[key];

    if (typeof value === "undefined") {
      if (showWarnings) {
        console.log(`Param "${key}" not found in translation`);
      }
      return "";
    }

    return DOMPurify.sanitize(value, {
      ALLOWED_ATTR: ["href", "rel", "target", "class", "id", "title"],
      ALLOWED_TAGS: ["a", "strong", "em", "b", "i", "span", "div"],
    });
  });
}

export const TranslationProvider = ({
  children,
  translations,
}: TranslationProviderProps) => {
  const [mergedTranslations, setMergedTranslations] = useState<Translations>(
    () => generateTranslations(defaultTranslations, translations),
  );

  useEffect(() => {
    const merged = generateTranslations(defaultTranslations, translations);
    setMergedTranslations(merged);
  }, [translations]);

  const t = useMemo(() => {
    return (
      key: string,
      params?: TranslateParams,
      options?: TranslateOptions,
    ): TranslationResult => {
      const translation = mergedTranslations[key];

      if (typeof translation === "undefined") {
        if (showWarnings) {
          console.log(`Translation for key "${key}" not found`);
        }
        return key;
      }

      if (translation === "") {
        return "";
      }

      const translationWithParams = interpolateParams(translation, params);
      const transformedTranslation =
        typeof options?.transform === "function"
          ? options.transform(translationWithParams)
          : translationWithParams;

      if (key.endsWith("-html")) {
        return parse(transformedTranslation);
      }

      return transformedTranslation;
    };
  }, [mergedTranslations]);

  const contextValue = useMemo(() => ({ t }), [t]);

  return (
    <TranslationContext value={contextValue}>{children}</TranslationContext>
  );
};
